import { CHOICE_COLORS } from '@shared/constants';
import { brandSubtitle, useAppConfig } from '../lib/config';
import { Shape } from './Shape';

/** Four answer shapes in Google colours, used as the logo mark. */
export function LogoMark({ className = 'size-8' }: { className?: string }) {
  return (
    <span className={`grid grid-cols-2 gap-[8%] ${className}`} aria-hidden>
      <Shape shape="triangle" fill={CHOICE_COLORS.red.bg} className="size-full" />
      <Shape shape="diamond" fill={CHOICE_COLORS.blue.bg} className="size-full" />
      <Shape shape="circle" fill={CHOICE_COLORS.yellow.bg} className="size-full" />
      <Shape shape="square" fill={CHOICE_COLORS.green.bg} className="size-full" />
    </span>
  );
}

/** Logo + app name + "by <credit> × <chapter>". `tone` picks colours for light or stage backgrounds. */
export function Brand({ tone = 'light', size = 'md' }: { tone?: 'light' | 'dark'; size?: 'sm' | 'md' | 'lg' }) {
  const config = useAppConfig();
  const { appName } = config;
  const subtitle = brandSubtitle(config);
  const mark = size === 'lg' ? 'size-12' : size === 'sm' ? 'size-6' : 'size-8';
  const title = size === 'lg' ? 'text-3xl' : size === 'sm' ? 'text-base' : 'text-xl';
  return (
    <span className="inline-flex items-center gap-3">
      <LogoMark className={mark} />
      <span className="flex flex-col leading-tight">
        <span className={`${title} font-extrabold tracking-tight ${tone === 'dark' ? 'text-white' : 'text-ink'}`}>
          {appName}
        </span>
        {subtitle && (
          <span className={`text-xs font-medium ${tone === 'dark' ? 'text-white/70' : 'text-ink-soft'}`}>
            {subtitle}
          </span>
        )}
      </span>
    </span>
  );
}

/** "Made by JT in collaboration with GDG Baroda", with the maker linked (footers of non-projector pages). */
export function CreditLine({ tone = 'light', className = '' }: { tone?: 'light' | 'dark'; className?: string }) {
  const { creditName, creditUrl, chapterName } = useAppConfig();
  if (!creditName && !chapterName) return null;
  const muted = tone === 'dark' ? 'text-white/60' : 'text-ink-soft';
  const strong = tone === 'dark' ? 'text-white/90' : 'text-ink';
  const maker = creditName ? (
    creditUrl ? (
      <a
        href={creditUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid ${strong}`}
      >
        {creditName}
      </a>
    ) : (
      <span className={`font-semibold ${strong}`}>{creditName}</span>
    )
  ) : null;
  return (
    <p className={`text-xs ${muted} ${className}`}>
      {maker ? (
        <>
          Made by {maker}
          {chapterName && <> in collaboration with {chapterName}</>}
        </>
      ) : (
        chapterName
      )}
    </p>
  );
}

/** Thin four-colour Google stripe. */
export function ColorStripe({ className = 'h-1' }: { className?: string }) {
  return (
    <div className={`flex w-full ${className}`} aria-hidden>
      <div className="flex-1 bg-g-blue" />
      <div className="flex-1 bg-g-red" />
      <div className="flex-1 bg-g-yellow" />
      <div className="flex-1 bg-g-green" />
    </div>
  );
}
